"use client";

import React from "react";
import { FlaskConical, Activity, Dna, Truck } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Link } from "@/components/common/Link";
import { useT } from "@/i18n";
const pillars = [{
  title: "Top Quality",
  description: "Our peptides are manufactured locally against the highest standards for synthesis, undergoing rigorous quality control measures to ensure their purity, stability, and consistency.",
  icon: FlaskConical,
  button: {
    text: "Certificate",
    href: "/CertificatesPage"
  }
}, {
  title: "Range of Products",
  description: "We offer a comprehensive and industry-leading peptide catalog. Whether you require standard peptides, blends, topical peptides, or customized orders, our experienced team is ready to assist you.",
  icon: Activity
}, {
  title: "Expert Customer Service",
  description: "We believe in building long-lasting relationships with our customers. Our dedicated customer support team is here to support you. We will do everything in our power to ensure that our customers are satisfied, even after products are delivered.",
  icon: Dna
}, {
  title: "Fast Delivery",
  description: "We strive to provide fast and reliable delivery, no matter where you are.",
  icon: Truck
}];
export default function TheOptimaProtocol() {
  const t = useT();
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="the-optima-protocol" className="bg-background text-foreground relative py-24 md:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none opacity-[0.15]" style={{
      backgroundImage: `
            linear-gradient(to right, var(--color-border) 1px, transparent 1px),
            linear-gradient(to bottom, var(--color-border) 1px, transparent 1px)
          `,
      backgroundSize: '4rem 4rem'
    }} aria-hidden="true" />

      {}
      <div className="absolute inset-0 z-0 pointer-events-none bg-[radial-gradient(circle_at_center,transparent_0%,var(--color-background)_100%)]" aria-hidden="true" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-24">
          <h3 className="text-3xl md:text-5xl font-default font-bold tracking-tight mb-6">
            {t("Why Research Choose Us")}
          </h3>
        </div>

        <div ref={ref} className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {pillars.map((pillar, i) => <Card key={i} data-index={i} className="bg-card/40 backdrop-blur-md border-border/50 hover:border-primary/40 transition-all duration-500 group relative overflow-hidden">
              {}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
              
              <CardHeader className="pb-4 relative z-10">
                <div className="h-14 w-14 rounded-xl bg-background border border-border/50 flex items-center justify-center mb-6 shadow-sm group-hover:scale-110 transition-transform duration-500 group-hover:border-primary/30 group-hover:shadow-[0_0_20px_rgba(var(--color-primary),0.2)]">
                  <pillar.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                </div>
                <CardTitle className="text-2xl font-default tracking-tight">
                  {t(pillar.title)}
                </CardTitle>
              </CardHeader>
              <CardContent className="relative z-10">
                <CardDescription className="text-base font-medium text-muted-foreground leading-relaxed">
                  {t(pillar.description)}
                </CardDescription>
                {pillar.button && <div className="mt-6">
                    <Link to={pillar.button.href}>
                      <Button className="w-full rounded-lg">
                        {t(pillar.button.text)}
                      </Button>
                    </Link>
                  </div>}
              </CardContent>
            </Card>)}
        </div>
      </div>
    </section>;
}